import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { LOCALES, type Locale } from './locales';

interface I18nState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

// Resolve a usable Storage, or fall back to in-memory.  localStorage may be
// absent (SSR), access-throwing (sandboxed iframe / privacy mode), or only
// partially present (some test environments) — guard all three so the persist
// middleware never crashes on setItem.
function safeLocaleStorage(): Storage {
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.setItem === 'function') {
      return localStorage;
    }
  } catch {
    /* access to localStorage can throw — fall through to in-memory */
  }
  const mem = new Map<string, string>();
  return {
    getItem: (k) => mem.get(k) ?? null,
    setItem: (k, v) => { mem.set(k, String(v)); },
    removeItem: (k) => { mem.delete(k); },
    clear: () => { mem.clear(); },
    key: (i) => Array.from(mem.keys())[i] ?? null,
    get length() { return mem.size; },
  } as Storage;
}

export const useI18nStore = create<I18nState>()(
  persist(
    (set) => ({ locale: 'ru', setLocale: (locale) => set({ locale }) }),
    { name: 'holzcom-locale', storage: createJSONStorage(safeLocaleStorage), version: 1 },
  ),
);

/**
 * Hook-style translator.  Returns a function that resolves keys in the
 * current locale, falling back to Russian source then to the key itself.
 *
 * Usage:
 *   const t = useT();
 *   <span>{t('tree.search.placeholder')}</span>
 */
export function useT(): (key: string) => string {
  const locale = useI18nStore((s) => s.locale);
  return (key: string) => {
    const dict = LOCALES[locale] || LOCALES.ru;
    return dict[key] ?? LOCALES.ru[key] ?? key;
  };
}

/** Out-of-component (e.g., one-shot toasts).  Reads current store snapshot. */
export function t(key: string): string {
  const locale = useI18nStore.getState().locale;
  const dict = LOCALES[locale] || LOCALES.ru;
  return dict[key] ?? LOCALES.ru[key] ?? key;
}

export type { Locale };
